// export default Navbar;
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  AppBar,
  Toolbar,
  Typography,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  IconButton,
  Avatar,
  Box,
  Container,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import {
  Search as SearchIcon,
  AccountCircle as AccountCircleIcon,
  NotificationsNone as NotificationsIcon,
  HelpOutline as HelpIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import { logoutUser } from "../utils/authUtils";

const Navbar = ({ sidebarOpen = true }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const tabItems = ["Overview"];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const handleLogout = () => {
    // Use the proper logout function that clears authentication state
    logoutUser(dispatch);
    navigate("/");
  };

  // Calculate sidebar width based on state
  const sidebarWidth = sidebarOpen ? 220 : 100;

  // Mobile Version
  if (isMobile) {
    return (
      <Box
        sx={{
          position: "sticky",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #f1f5f9",
          px: 2,
          py: 1.3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography
            variant="h6"
            component="h1"
            sx={{
              color: "#1e293b",
              fontWeight: 600,
              fontSize: "1.1rem",
            }}
          >
            Main Dashboard
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              sx={{
                padding: "6px",
                color: "#A3AED0",
              }}
            >
              <NotificationsIcon sx={{ fontSize: "20px" }} />
            </IconButton>
            <Avatar
              sx={{
                width: 28,
                height: 28,
                backgroundColor: "#ffffff",
                color: "#A3AED0",
                fontSize: "14px",
                ml: 0.5,
              }}
            >
              <AccountCircleIcon sx={{ fontSize: "18px" }} />
            </Avatar>
            <IconButton
              onClick={handleLogout}
              sx={{
                padding: "6px",
                color: "#64748b",
                ml: 0.5,
                "&:hover": {
                  backgroundColor: "#f8fafc",
                  color: "#475569",
                },
              }}
            >
              <LogoutIcon sx={{ fontSize: "20px" }} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTabs-indicator": {
                backgroundColor: "#2B3674",
                height: 3,
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "0.85rem",
                fontWeight: 500,
                color: "#64748b",
                minWidth: "auto",
                padding: "8px 16px",
                "&.Mui-selected": {
                  color: "#2B3674",
                  fontWeight: 600,
                },
              },
            }}
          >
            {tabItems.map((item, index) => (
              <Tab key={index} label={item} />
            ))}
          </Tabs>
        </Box>
      </Box>
    );
  }

  // Desktop Version - Dynamic width based on sidebar state
  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: "#ffffff",
        borderBottom: "1px solid #f1f5f9",
        // Dynamic left offset based on sidebar state
        left: { lg: `${sidebarWidth}px` },
        width: {
          xs: "100%",
          lg: `calc(100% - ${sidebarWidth}px)`,
        },
        top: 0,
        zIndex: 1100,
        transition: "left 0.3s ease, width 0.3s ease", // Smooth transition
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          px: { xs: 2, lg: 3 },
          width: "100%",
        }}
      >
        <Toolbar
          sx={{
            padding: "0 !important",
            minHeight: "65px",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          {/* Left side - Title and Navigation */}
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}
          >
            <Typography
              variant="h5"
              component="h1"
              sx={{
                color: "#1e293b",
                fontWeight: 600,
                fontSize: { xs: "1.1rem", lg: "1.35rem" },
                mt: "2%",
                mb: 0.5,
                display: "flex",
                textAlign: "left",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Main Dashboard
            </Typography>

            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                "& .MuiTabs-indicator": {
                  backgroundColor: "#2B3674",
                  height: 5,
                },
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "#64748b",
                  minWidth: "auto",
                  padding: "6px 16px",
                  marginRight: { xs: "8px", lg: "24px" },
                  "&.Mui-selected": {
                    color: "#2B3674",
                  },
                },
              }}
            >
              {tabItems.map((item, index) => (
                <Tab key={index} label={item} />
              ))}
            </Tabs>
          </Box>

          {/* Right side - Logout Button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              ml: 2,
            }}
          >
            <Button
              onClick={handleLogout}
              startIcon={<LogoutIcon />}
              sx={{
                textTransform: "none",
                color: "#64748b",
                borderColor: "#e2e8f0",
                fontSize: "0.875rem",
                fontWeight: 500,
                padding: "8px 20px",
                borderRadius: "8px",
                whiteSpace: "nowrap",
                "&:hover": {
                  backgroundColor: "#f8fafc",
                  borderColor: "#cbd5e1",
                  color: "#475569",
                },
              }}
              variant="outlined"
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;
